// WebProfit Guided Onboarding · modular tutorial engine contract
// STATUS: BLOCK_8_CATALOG_HANDOFF_POLISHED · resilient spotlight + resumable modular roadmap.
// TEST STATE: STATIC_PASS target; staging runtime still required after deployment.
// DESIGN: localStorage is the non-blocking source of truth. Analytics/network failures
// must never stop navigation, item creation, stock, cash or any business operation.
// PRIVACY: tutorial events carry structural metadata only; never field values, passwords,
// tokens, imported row contents, invoice contents or free text written by the user.

export const DASHBOARD_TUTORIAL_EVENT = "webprofit:dashboard-tutorial";
export const DASHBOARD_TUTORIAL_VERSION = 5;

const STORAGE_PREFIX = "webprofit:dashboard-tutorial";

export const DASHBOARD_ONBOARDING_STAGES = Object.freeze([
  { key: "navigation_intro", label: "Aprender a moverte", kind: "orientation" },
  { key: "business_context", label: "Entender tu negocio", kind: "diagnosis" },
  { key: "roadmap_intro", label: "Ver tu puesta a punto", kind: "orientation" },
  { key: "site_setup", label: "Conocer y ajustar Mi web", kind: "setup" },
  { key: "catalog", label: "Construir o importar el catálogo", kind: "setup" },
  { key: "site_activation", label: "Ver y compartir la web", kind: "activation" },
  { key: "initial_stock", label: "Resolver el stock inicial", kind: "physical_work" },
  { key: "historical_data", label: "Decidir qué historia migrar", kind: "migration" },
  { key: "initial_finance", label: "Crear cajas y cuentas", kind: "setup" },
  { key: "access_control", label: "Definir equipo y accesos", kind: "security" },
  { key: "first_sale", label: "Registrar la primera venta", kind: "practice" },
  { key: "first_purchase", label: "Registrar la primera compra", kind: "practice" },
  { key: "fiscal_setup", label: "Preparar facturación", kind: "integration" },
  { key: "payments_setup", label: "Conectar cobros online", kind: "integration" },
  { key: "daily_routine", label: "Entender la rutina diaria", kind: "habit" },
]);

const BLOCKS = Object.freeze({
  navigation_intro: {
    key: "navigation_intro",
    stageKey: "navigation_intro",
    label: "Cómo moverte",
    eyebrow: "0 · Antes de empezar",
    title: "Primero ubicáte: ésta es la navegación de tu negocio",
    description: "La barra principal cambia de área; debajo aparecen las herramientas de esa área y, a la derecha, están proyecto, avisos y cuenta. No hace falta memorizar nada: WebProfit te va a ir llevando a lo importante.",
    route: (projectKey) => `/dashboard/proyectos/${projectKey}/inicio`,
    selector: '[data-wp-tutorial-target="panel-primary-navigation"]',
    actionLabel: "Mostrarme la navegación",
    continueLabel: "Ya sé cómo moverme",
    completion: "manual",
    required: true,
  },
  business_context: {
    key: "business_context",
    stageKey: "business_context",
    label: "Tu negocio",
    eyebrow: "1 · Empecemos",
    title: "Dos respuestas y te armamos el recorrido",
    description: "Confirmá qué tipo de negocio tenés y qué querés resolver primero. No hace falta entrar a Configuración ni entender módulos técnicos.",
    actionLabel: "Preparar mi recorrido",
    continueLabel: "Preparar mi recorrido",
    completion: "inline_form",
    required: true,
  },
  roadmap_intro: {
    key: "roadmap_intro",
    stageKey: "roadmap_intro",
    label: "Tu puesta a punto",
    eyebrow: "2 · Tu recorrido",
    title: "Vamos a preparar el negocio por bloques",
    description: "Primero entendés Mi web, después armás el catálogo y recién entonces conectamos stock, caja y operación. No hace falta terminar todo hoy: cada bloque se puede retomar.",
    actionLabel: "Empezar la puesta a punto",
    continueLabel: "Empezar",
    completion: "manual",
    required: true,
  },
  site_setup: {
    key: "site_setup",
    stageKey: "site_setup",
    label: "Mi web",
    eyebrow: "3 · Mi web",
    title: "Ésta es la puerta de entrada a tu página",
    description: "Primero mirá tu link público y dónde se abre el editor. Podés diseñarla ahora, entrar con guía o volver después: no frenamos catálogo ni gestión por tener la web incompleta.",
    route: (projectKey) => `/dashboard/proyectos/${projectKey}/pagina`,
    selector: '[data-wp-tutorial-target="site-antechamber-editor-actions"]',
    actionLabel: "Ver cómo se diseña Mi web",
    continueLabel: "Ya me ubiqué, seguir",
    completion: "manual",
    optional: true,
    skipLabel: "Seguir con el catálogo",
    feature: "landing",
  },
  catalog: {
    key: "catalog",
    stageKey: "catalog",
    label: "Catálogo",
    eyebrow: "4 · Base operativa",
    title: "Primero carguemos un ítem juntos",
    description: "Antes de importar una planilla, te mostramos cómo piensa WebProfit un producto o servicio. Vas a recorrer tipo, nombre, categoría, precio, variantes, uso, facturación y publicación con ejemplos según tu rubro.",
    route: (projectKey, project) => project?.modules?.system
      ? `/dashboard/proyectos/${projectKey}/sistema/items`
      : `/dashboard/proyectos/${projectKey}/productos`,
    // WP-ONBOARDING-CATALOG-HANDOFF
    // STATUS: STATIC_PASS / STAGING_PENDING
    // The selector intentionally resolves to the Product/Service choice when the
    // first-publication card is visible, and to "Nuevo ítem" on established catalogs.
    selector: '[data-wp-tutorial-target="items-create-primary"]',
    actionLabel: "Crear mi primer ítem",
    waitingLabel: "Terminá de crear un ítem para seguir",
    unlockAfterTargetClick: true,
    completion: "metric",
    completeOnMetric: ["item_entry_completed"],
    coachMode: "item_form",
    required: true,
    featureAny: ["products", "system"],
  },
  site_activation: {
    key: "site_activation",
    stageKey: "site_activation",
    label: "Tu primera web real",
    eyebrow: "Después del primer ítem",
    title: "Mirá esto: ya tenés algo real para mostrar",
    description: "Éste es el link público de tu negocio. Abrilo para ver cómo quedó el catálogo, compartilo si querés y después decidí si diseñás la web ahora, con guía, o seguís configurando el negocio.",
    route: (projectKey) => `/dashboard/proyectos/${projectKey}/pagina?momento=primer-item`,
    selector: '[data-wp-tutorial-target="site-antechamber-hero"]',
    actionLabel: "Ver mi web",
    continueLabel: "Ya entendí dónde está mi web",
    completion: "manual",
    required: true,
    feature: "landing",
  },
  initial_stock: {
    key: "initial_stock",
    stageKey: "initial_stock",
    label: "Conteo físico",
    eyebrow: "5 · Puesta a punto",
    title: "Lo cargado en sistema tiene que encontrarse con la realidad física",
    description: "Si manejás stock, contalo por etapas. Podés pausar y continuar; medimos tiempo activo, espera, avance y recuperación sin guardar cantidades escritas como telemetría.",
    route: (projectKey) => `/dashboard/proyectos/${projectKey}/sistema/stock`,
    selector: '[data-wp-tutorial-target="stock-count"]',
    actionLabel: "Empezar conteo",
    waitingLabel: "Completá o pausá el conteo cuando necesites",
    unlockAfterTargetClick: true,
    completion: "metric",
    completeOnMetric: ["stock_count_completed"],
    optional: true,
    skipLabel: "No manejo stock físico",
    feature: "system",
    profileAny: ["commerce", "restaurant"],
    goalAny: ["sellProducts", "controlBusiness"],
  },
  historical_data: {
    key: "historical_data",
    stageKey: "historical_data",
    label: "Historia anterior",
    eyebrow: "6 · Migración",
    title: "No todo lo histórico debe alterar el presente",
    description: "Acá decidimos qué comprobantes o movimientos anteriores conviene conservar como referencia. Este bloque queda deliberadamente diferible hasta cerrar bien la estrategia de facturas históricas.",
    route: (projectKey) => `/dashboard/proyectos/${projectKey}/sistema/movimientos`,
    actionLabel: "Ver movimientos",
    continueLabel: "Lo voy a definir después",
    completion: "manual",
    optional: true,
    deferPreferred: true,
    deferLabel: "Definirlo más adelante",
    feature: "system",
  },
  initial_finance: {
    key: "initial_finance",
    stageKey: "initial_finance",
    label: "Cajas y cuentas",
    eyebrow: "7 · Dinero",
    title: "Decinos dónde está y por dónde se mueve la plata",
    description: "Creá efectivo, banco, billetera o Mercado Pago como lugares reales de dinero. Después cada venta, compra o gasto puede impactar donde corresponde.",
    route: (projectKey) => `/dashboard/proyectos/${projectKey}/sistema/caja`,
    selector: '[data-wp-tutorial-target="cash-create"]',
    actionLabel: "Crear primera caja",
    waitingLabel: "Terminá de crear una caja o cuenta",
    unlockAfterTargetClick: true,
    completion: "metric",
    completeOnMetric: ["cash_register_completed"],
    required: true,
    feature: "system",
  },
  access_control: {
    key: "access_control",
    stageKey: "access_control",
    label: "Equipo y accesos",
    eyebrow: "8 · Personas",
    title: "Cada persona debería ver y tocar sólo lo que necesita",
    description: "Si alguien controla caja, podés darle alcance de cajero sin permitir gestión general ni eliminación de ítems. Owner y admin quedan para decisiones sensibles.",
    route: (projectKey) => `/dashboard/proyectos/${projectKey}/configuracion/accesos`,
    selector: '[data-wp-tutorial-target="access-create"]',
    actionLabel: "Configurar un acceso",
    waitingLabel: "Agregá un miembro o revisá los permisos",
    unlockAfterTargetClick: true,
    completion: "metric_or_manual",
    completeOnMetric: ["access_member_completed"],
    optional: true,
    skipLabel: "Trabajo solo por ahora",
  },
  first_sale: {
    key: "first_sale",
    stageKey: "first_sale",
    label: "Primera venta",
    eyebrow: "9 · Operación diaria",
    title: "Registremos una venta como la harías todos los días",
    description: "Ésta es la victoria que conecta catálogo, stock, caja y contabilidad. No hace falta inventar datos: podés recorrer el formulario y guardar recién cuando tengas una venta real.",
    route: (projectKey) => `/dashboard/proyectos/${projectKey}/sistema/movimientos?vista=ventas`,
    selector: '[data-wp-tutorial-target="sale-create"]',
    actionLabel: "Registrar venta",
    waitingLabel: "Guardá una venta real o marcá el paso cuando ya entiendas el flujo",
    continueLabel: "Entendido, lo haré con una venta real",
    unlockAfterTargetClick: true,
    completion: "metric_or_manual",
    completeOnMetric: ["movement_sale_completed"],
    required: true,
    feature: "system",
  },
  first_purchase: {
    key: "first_purchase",
    stageKey: "first_purchase",
    label: "Compra de mercadería",
    eyebrow: "10 · Reposición",
    title: "Ahora veamos cómo entra mercadería al negocio",
    description: "Una compra bien registrada puede actualizar costo, stock pendiente/recibido, caja y cuentas sin duplicar trabajo.",
    route: (projectKey) => `/dashboard/proyectos/${projectKey}/sistema/movimientos`,
    selector: '[data-wp-tutorial-target="movement-create"]',
    actionLabel: "Registrar compra",
    waitingLabel: "Terminá de registrar la compra",
    unlockAfterTargetClick: true,
    completion: "metric",
    completeOnMetric: ["movement_purchase_completed"],
    optional: true,
    skipLabel: "No compro mercadería ahora",
    feature: "system",
    profileAny: ["commerce", "restaurant"],
    goalAny: ["sellProducts", "controlBusiness"],
  },
  fiscal_setup: {
    key: "fiscal_setup",
    stageKey: "fiscal_setup",
    label: "Facturación",
    eyebrow: "11 · Fiscal",
    title: "Facturar es un bloque independiente de vender",
    description: "Si te interesa, acá se conecta ARCA y después las ventas confirmadas pueden emitirse con trazabilidad. Si todavía no lo necesitás, lo dejamos pendiente.",
    route: (projectKey) => `/dashboard/proyectos/${projectKey}/configuracion/facturacion`,
    selector: '[data-wp-tutorial-target="fiscal-connect"]',
    actionLabel: "Revisar facturación",
    waitingLabel: "Probá la conexión cuando tengas las credenciales",
    unlockAfterTargetClick: true,
    completion: "metric_or_manual",
    completeOnMetric: ["fiscal_connection_completed"],
    optional: true,
    skipLabel: "Lo configuro cuando facture",
  },
  payments_setup: {
    key: "payments_setup",
    stageKey: "payments_setup",
    label: "Mercado Pago",
    eyebrow: "12 · Cobros online",
    title: "Los cobros online también son una pieza enchufable",
    description: "Conectá Mercado Pago sólo si lo necesitás ahora. La operación diaria funciona aunque este bloque quede para más adelante.",
    route: (projectKey) => `/dashboard/proyectos/${projectKey}/configuracion/ecommerce`,
    selector: '[data-wp-tutorial-target="mp-connect"]',
    actionLabel: "Revisar Mercado Pago",
    waitingLabel: "Conectalo cuando tengas las credenciales",
    unlockAfterTargetClick: true,
    completion: "metric_or_manual",
    completeOnMetric: ["payment_connection_completed"],
    optional: true,
    skipLabel: "No necesito cobros online",
    featureAny: ["ecommerce", "payments"],
  },
  daily_routine: {
    key: "daily_routine",
    stageKey: "daily_routine",
    label: "Tu rutina",
    eyebrow: "13 · Listo para operar",
    title: "Desde acá WebProfit deja de ser tutorial y pasa a ser rutina",
    description: "Venta diaria, compras, caja, stock y facturación son bloques independientes. Podés volver a cualquier guía desde Ayuda y tutoriales sin reiniciar el negocio.",
    route: (projectKey) => `/dashboard/proyectos/${projectKey}/inicio`,
    actionLabel: "Ir al inicio",
    continueLabel: "Terminar puesta a punto",
    completion: "manual",
    required: true,
  },
});

export const DASHBOARD_TUTORIALS = Object.freeze([
  {
    key: "welcome",
    label: "Puesta a punto completa",
    shortLabel: "Puesta a punto",
    description: "Un recorrido corto y modular: entendemos tu negocio, te ubicamos y después te acompañamos en cada tarea real.",
    stages: DASHBOARD_ONBOARDING_STAGES.map((stage) => stage.key),
  },
  {
    key: "sale",
    label: "¿Cómo registro una venta?",
    shortLabel: "Registrar una venta",
    description: "Guía práctica para registrar la operación diaria y entender sus impactos.",
    stages: ["first_sale"],
  },
  {
    key: "purchase",
    label: "¿Cómo ingreso mercadería?",
    shortLabel: "Comprar mercadería",
    description: "Recorrido para registrar una compra y llevarla hasta stock/caja correctamente.",
    stages: ["first_purchase"],
  },
  {
    key: "item",
    label: "¿Cómo cargo o importo ítems?",
    shortLabel: "Catálogo",
    description: "Carga manual, servicios o importación masiva con medición de fricción.",
    stages: ["catalog"],
  },
  {
    key: "service",
    label: "¿Cómo cargo un servicio?",
    shortLabel: "Cargar un servicio",
    description: "Usa el mismo catálogo, pero sin forzar stock físico ni campos que no correspondan.",
    stages: ["catalog"],
  },
  {
    key: "site",
    label: "¿Cómo ajusto Mi web?",
    shortLabel: "Mi web",
    description: "Te lleva al editor visual sin obligarte a terminar la página antes de gestionar el negocio.",
    stages: ["site_setup"],
  },
  {
    key: "stock",
    label: "¿Cómo hago el conteo inicial?",
    shortLabel: "Conteo inicial",
    description: "Guía de inventario físico que puede pausarse y retomarse.",
    stages: ["initial_stock"],
  },
  {
    key: "cash",
    label: "¿Cómo configuro cajas?",
    shortLabel: "Cajas y cuentas",
    description: "Crea los lugares reales donde entra y sale dinero.",
    stages: ["initial_finance"],
  },
  {
    key: "access",
    label: "¿Cómo doy acceso al equipo?",
    shortLabel: "Equipo y permisos",
    description: "Roles y alcances para que cada persona vea solamente lo necesario.",
    stages: ["access_control"],
  },
  {
    key: "fiscal",
    label: "¿Cómo preparo la facturación?",
    shortLabel: "Facturación",
    description: "Guía independiente para revisar la conexión fiscal cuando realmente la necesites.",
    stages: ["fiscal_setup"],
  },
  {
    key: "payments",
    label: "¿Cómo conecto Mercado Pago?",
    shortLabel: "Mercado Pago",
    description: "Conexión de cobros online como bloque opt-in, separada de la operación diaria.",
    stages: ["payments_setup"],
  },
]);

function safeProjectKey(project) {
  return String(project?.slug || project?.id || project?._id || "").trim();
}

function storageKey(project) {
  const projectKey = safeProjectKey(project);
  return projectKey ? `${STORAGE_PREFIX}:${projectKey}` : STORAGE_PREFIX;
}

function safeParse(value) {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch (_) {
    return null;
  }
}

function createSessionId() {
  if (typeof window !== "undefined" && window.crypto && typeof window.crypto.randomUUID === "function") {
    return window.crypto.randomUUID();
  }
  return `tutorial-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function projectHasFeature(project, feature) {
  if (!feature) return true;
  if (project?.modules?.[feature] === true) return true;
  if (project?.capabilities?.[feature] === true) return true;
  return false;
}

function blockApplies(project, block) {
  if (!block) return false;
  if (block.feature && !projectHasFeature(project, block.feature)) return false;
  if (Array.isArray(block.featureAny) && block.featureAny.length) {
    if (!block.featureAny.some((feature) => projectHasFeature(project, feature))) return false;
  }

  // Personalización sin esconder funciones: sólo evita meter trabajo físico
  // irrelevante en la misión principal (por ejemplo, stock/compra para un
  // estudio de servicios). El usuario puede lanzar cualquier tutorial manual
  // desde Ayuda aunque no forme parte de la puesta a punto recomendada.
  if (Array.isArray(block.profileAny) && block.profileAny.length) {
    const profile = String(project?.businessProfile || "").trim().toLowerCase();
    const goals = new Set(Array.isArray(project?.onboarding?.businessGoals) ? project.onboarding.businessGoals : []);
    const profileMatches = profile ? block.profileAny.includes(profile) : false;
    const goalMatches = Array.isArray(block.goalAny) && block.goalAny.some((goal) => goals.has(goal));
    if (profile && !profileMatches && !goalMatches) return false;
  }
  return true;
}

export function tutorialByKey(key) {
  return DASHBOARD_TUTORIALS.find((tutorial) => tutorial.key === key) || DASHBOARD_TUTORIALS[0];
}

export function tutorialBlockByKey(key) {
  return BLOCKS[key] || null;
}

export function onboardingStageByKey(key) {
  return DASHBOARD_ONBOARDING_STAGES.find((stage) => stage.key === key) || null;
}

export function buildDashboardTutorialPlan(project, tutorialKey = "welcome") {
  const tutorial = tutorialByKey(tutorialKey);
  return (tutorial.stages || [])
    .map((key) => tutorialBlockByKey(key))
    .filter((block) => block && blockApplies(project, block))
    .map((block) => block.key);
}

export function dashboardTutorialBlockRoute(block, project) {
  if (!block) return "";
  const projectKey = safeProjectKey(project);
  if (!projectKey) return "";
  return typeof block.route === "function" ? block.route(projectKey, project) : String(block.route || "");
}

export function loadDashboardTutorialState(project) {
  if (typeof window === "undefined") return null;
  try {
    const stored = safeParse(window.localStorage.getItem(storageKey(project)));
    if (!stored) return null;
    if (Number(stored.version || 0) === DASHBOARD_TUTORIAL_VERSION && Array.isArray(stored.planKeys)) {
      return stored;
    }

    // MIGRATION: Block 0/1 stored a simpler v2 state. A user who had the old
    // tutorial open could otherwise land on the removed `data_origin` stage and
    // see no coach at all. We migrate locally and never require a server call.
    const tutorialKey = tutorialByKey(stored.tutorialKey || "welcome").key;
    const planKeys = buildDashboardTutorialPlan(project, tutorialKey);
    const legacyStageMap = { data_origin: "catalog" };
    const requestedKey = legacyStageMap[stored.stageKey] || stored.stageKey || planKeys[0] || "";
    const requestedIndex = Math.max(0, planKeys.indexOf(requestedKey));
    const step = planKeys.length ? (requestedIndex >= 0 ? requestedIndex : 0) : 0;
    const blockKey = planKeys[step] || "";
    const migrated = {
      ...stored,
      version: DASHBOARD_TUTORIAL_VERSION,
      tutorialKey,
      planKeys,
      step,
      stageKey: blockKey,
      blockKey,
      engagedBlockKey: "",
      completedBlocks: Array.isArray(stored.completedBlocks) ? stored.completedBlocks : [],
      skippedBlocks: Array.isArray(stored.skippedBlocks) ? stored.skippedBlocks : [],
      deferredBlocks: Array.isArray(stored.deferredBlocks) ? stored.deferredBlocks : [],
      migratedAt: new Date().toISOString(),
      active: Boolean(blockKey) && stored.active === true,
    };
    persistDashboardTutorialState(project, migrated);
    return migrated;
  } catch (_) {
    return null;
  }
}

export function persistDashboardTutorialState(project, nextState) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey(project), JSON.stringify(nextState));
  } catch (_) {
    // Persistence is convenience. Tutorial execution must never depend on it.
  }
}

export function emitDashboardTutorialEvent(type, session = null, detail = {}) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(DASHBOARD_TUTORIAL_EVENT, {
    detail: { type, session, ...detail },
  }));
}

function sessionWithCurrentBlock(session) {
  const planKeys = Array.isArray(session?.planKeys) ? session.planKeys : [];
  const step = Math.max(0, Number(session?.step || 0));
  const blockKey = planKeys[step] || session?.stageKey || "";
  return { ...session, stageKey: blockKey, blockKey };
}

export function startDashboardTutorial(project, tutorialKey = "welcome", source = "manual") {
  const tutorial = tutorialByKey(tutorialKey);
  const previous = loadDashboardTutorialState(project);
  const planKeys = buildDashboardTutorialPlan(project, tutorial.key);
  const firstBlockKey = planKeys[0] || "";
  const now = new Date().toISOString();
  const session = {
    version: DASHBOARD_TUTORIAL_VERSION,
    sessionId: createSessionId(),
    projectKey: safeProjectKey(project),
    tutorialKey: tutorial.key,
    active: Boolean(firstBlockKey),
    paused: false,
    source,
    step: 0,
    stageKey: firstBlockKey,
    blockKey: firstBlockKey,
    planKeys,
    completedBlocks: [],
    skippedBlocks: [],
    deferredBlocks: [],
    engagedBlockKey: "",
    startedAt: now,
    blockStartedAt: now,
  };
  persistDashboardTutorialState(project, session);
  emitDashboardTutorialEvent(previous?.active ? "restart" : "start", session, {
    previousSessionId: previous?.sessionId || "",
    blockKey: firstBlockKey,
    planLength: planKeys.length,
  });
  if (firstBlockKey) emitDashboardTutorialEvent("block_started", session, { blockKey: firstBlockKey });
  return session;
}

export function resumeDashboardTutorial(project, source = "manual_resume") {
  const current = loadDashboardTutorialState(project);
  if (!current || current.completedAt) return current;
  const next = sessionWithCurrentBlock({
    ...current,
    active: true,
    paused: false,
    resumedAt: new Date().toISOString(),
    resumeSource: source,
    blockStartedAt: current.blockStartedAt || new Date().toISOString(),
  });
  persistDashboardTutorialState(project, next);
  emitDashboardTutorialEvent("resume", next, { reason: source, blockKey: next.blockKey });
  return next;
}

export function pauseDashboardTutorial(project, reason = "manual_pause") {
  const current = loadDashboardTutorialState(project) || {};
  const next = {
    ...current,
    active: false,
    paused: true,
    pausedAt: new Date().toISOString(),
    pauseReason: reason,
  };
  persistDashboardTutorialState(project, next);
  emitDashboardTutorialEvent("pause", next, { reason, blockKey: next.blockKey || next.stageKey || "" });
  return next;
}

export function refreshDashboardTutorialPlan(project, reason = "context_update") {
  const current = loadDashboardTutorialState(project);
  if (!current?.active) return current;
  const planKeys = buildDashboardTutorialPlan(project, current.tutorialKey || "welcome");
  const currentKey = current?.planKeys?.[current.step] || current.blockKey || current.stageKey || planKeys[0] || "";
  const nextIndex = planKeys.indexOf(currentKey);
  const step = nextIndex >= 0 ? nextIndex : 0;
  const blockKey = planKeys[step] || "";
  const next = {
    ...current,
    planKeys,
    step,
    stageKey: blockKey,
    blockKey,
    planUpdatedAt: new Date().toISOString(),
    planUpdateReason: reason,
  };
  persistDashboardTutorialState(project, next);
  emitDashboardTutorialEvent("plan_updated", next, { reason, blockKey, planLength: planKeys.length });
  return next;
}

export function setDashboardTutorialStep(project, step, stageKey = "", reason = "engine") {
  const current = loadDashboardTutorialState(project);
  if (!current?.active) return current;
  const next = sessionWithCurrentBlock({
    ...current,
    step: Math.max(0, Number(step || 0)),
    stageKey: stageKey || current.stageKey || "",
    engagedBlockKey: "",
    stepChangedAt: new Date().toISOString(),
    blockStartedAt: new Date().toISOString(),
    stepChangeReason: reason,
  });
  persistDashboardTutorialState(project, next);
  emitDashboardTutorialEvent("step", next, { reason, blockKey: next.blockKey });
  emitDashboardTutorialEvent("block_started", next, { reason, blockKey: next.blockKey });
  return next;
}

export function engageDashboardTutorialTarget(project, blockKey, detail = {}) {
  const current = loadDashboardTutorialState(project);
  if (!current?.active) return current;
  const next = {
    ...current,
    engagedBlockKey: String(blockKey || current.blockKey || current.stageKey || ""),
    targetEngagedAt: new Date().toISOString(),
  };
  persistDashboardTutorialState(project, next);
  emitDashboardTutorialEvent("target_engaged", next, { blockKey: next.engagedBlockKey, ...detail });
  return next;
}

export function advanceDashboardTutorial(project, result = "completed", detail = {}) {
  const current = loadDashboardTutorialState(project);
  if (!current?.active) return current;
  const planKeys = Array.isArray(current.planKeys) ? current.planKeys : [];
  const currentKey = planKeys[current.step] || current.blockKey || current.stageKey || "";
  const completedBlocks = new Set(current.completedBlocks || []);
  const skippedBlocks = new Set(current.skippedBlocks || []);
  const deferredBlocks = new Set(current.deferredBlocks || []);
  if (result === "completed") completedBlocks.add(currentKey);
  if (result === "skipped") skippedBlocks.add(currentKey);
  if (result === "deferred") deferredBlocks.add(currentKey);

  const blockStartedMs = Date.parse(current.blockStartedAt || current.stepChangedAt || current.startedAt || "");
  const dwellMs = Number.isFinite(blockStartedMs) ? Math.max(0, Date.now() - blockStartedMs) : 0;
  emitDashboardTutorialEvent(
    result === "completed" ? "block_completed" : result === "deferred" ? "block_deferred" : "block_skipped",
    current,
    { blockKey: currentKey, result, dwellMs, ...detail },
  );
  const nextStep = current.step + 1;
  if (nextStep >= planKeys.length) {
    return completeDashboardTutorial(project, "roadmap_completed", {
      completedBlocks: [...completedBlocks],
      skippedBlocks: [...skippedBlocks],
      deferredBlocks: [...deferredBlocks],
    });
  }
  const nextKey = planKeys[nextStep];
  const next = {
    ...current,
    step: nextStep,
    stageKey: nextKey,
    blockKey: nextKey,
    engagedBlockKey: "",
    completedBlocks: [...completedBlocks],
    skippedBlocks: [...skippedBlocks],
    deferredBlocks: [...deferredBlocks],
    stepChangedAt: new Date().toISOString(),
    blockStartedAt: new Date().toISOString(),
    targetEngagedAt: null,
  };
  persistDashboardTutorialState(project, next);
  emitDashboardTutorialEvent("step", next, { reason: result, blockKey: nextKey });
  emitDashboardTutorialEvent("block_started", next, { reason: result, blockKey: nextKey });
  return next;
}

export function markDashboardTutorialBlocked(project, reason = "required_action", detail = {}) {
  const current = loadDashboardTutorialState(project);
  if (!current?.active) return current;
  emitDashboardTutorialEvent("blocked", current, {
    reason,
    blockKey: current.blockKey || current.stageKey || "",
    ...detail,
  });
  return current;
}

export function reportDashboardTutorialMetric(project, metricType, detail = {}) {
  const current = loadDashboardTutorialState(project);
  if (!current?.active) return current;
  emitDashboardTutorialEvent("metric", current, { metricType, ...detail });
  return current;
}

export function reportActiveDashboardTutorialMetric(metricType, detail = {}) {
  emitDashboardTutorialEvent("metric", null, { metricType, ...detail });
}

export function completeDashboardTutorial(project, reason = "completed", extra = {}) {
  const current = loadDashboardTutorialState(project) || {};
  const session = {
    ...current,
    ...extra,
    active: false,
    paused: false,
    completedAt: new Date().toISOString(),
    completionReason: reason,
    engagedBlockKey: "",
  };
  persistDashboardTutorialState(project, session);
  emitDashboardTutorialEvent("complete", session, { reason });
  return session;
}

export function stopDashboardTutorial(project, reason = "manual") {
  const current = loadDashboardTutorialState(project) || {};
  const session = {
    ...current,
    active: false,
    paused: false,
    stoppedAt: new Date().toISOString(),
    stopReason: reason,
    engagedBlockKey: "",
  };
  persistDashboardTutorialState(project, session);
  emitDashboardTutorialEvent("stop", session, { reason });
  return session;
}
